import { FiMapPin } from 'react-icons/fi';

export const basicPrepositionsData = {
  courseId: 'basic-prepositions',
  courseType: 'grammar',
  title: 'Basic Prepositions',
  icon: FiMapPin,
  overview: {
    impact: 'Preposition accuracy directly affects grammar band scores',
    focus: 'Prepositions of time, place, and movement',
    uses: 'All IELTS sections',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Prepositions of Time',
      content: `<p>Three key prepositions for time:</p>`,
      table: {
        headers: [
          'Preposition',
          'Use',
          'Examples'
        ],
        rows: [
          [
            'in',
            'months, years, seasons, parts of day',
            'in January, in 2024, in summer, in the morning'
          ],
          [
            'on',
            'days, dates, specific days',
            'on Monday, on 5th March, on my birthday'
          ],
          [
            'at',
            'precise times, festivals, night',
            'at 9 o\'clock, at Christmas, at night'
          ]
        ]
      }
    },
    {
      title: 'Prepositions of Place',
      content: `<p>Where things are located:</p>`,
      table: {
        headers: [
          'Preposition',
          'Use',
          'Examples'
        ],
        rows: [
          [
            'in',
            'enclosed spaces, countries, cities',
            'in the box, in France, in London'
          ],
          [
            'on',
            'surfaces, streets, floors',
            'on the table, on Oxford Street, on the 3rd floor'
          ],
          [
            'at',
            'specific points, addresses',
            'at the door, at 42 Park Lane, at the bus stop'
          ]
        ]
      }
    },
    {
      title: 'Prepositions of Movement',
      content: `<p>Describing direction and movement:</p>`,
      points: [
        'to: direction towards → \'I walked to the shop.\'',
        'into: entering an enclosed space → \'She went into the room.\'',
        'through: passing from one side to another → \'We drove through the tunnel.\'',
        'across: from one side to the other (surface) → \'They swam across the river.\'',
        'along: following a line → \'We walked along the beach.\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Correct Preposition',
      type: 'select',
      instructions: 'Select in, on, or at.',
      questions: [
        {
          text: 'I was born ___ 1999.',
          options: [
            'in',
            'on',
            'at'
          ],
          answer: 'in',
          explanation: 'Years use \'in\'.'
        },
        {
          text: 'The meeting is ___ Monday.',
          options: [
            'on',
            'in',
            'at'
          ],
          answer: 'on',
          explanation: 'Days use \'on\'.'
        },
        {
          text: 'She arrives ___ 3 pm.',
          options: [
            'at',
            'in',
            'on'
          ],
          answer: 'at',
          explanation: 'Precise times use \'at\'.'
        },
        {
          text: 'There\'s a cat ___ the roof.',
          options: [
            'on',
            'in',
            'at'
          ],
          answer: 'on',
          explanation: 'Surfaces use \'on\'.'
        },
        {
          text: 'He lives ___ Paris.',
          options: [
            'in',
            'on',
            'at'
          ],
          answer: 'in',
          explanation: 'Cities use \'in\'.'
        }
      ]
    },
    {
      title: 'Prepositions of Place',
      type: 'select',
      instructions: 'Choose the correct preposition of place: in, on, or at.',
      questions: [
        {
          text: 'The children are playing ___ the garden.',
          options: [
            'in',
            'on',
            'at'
          ],
          answer: 'in',
          explanation: 'Gardens are enclosed spaces → \'in the garden\'.'
        },
        {
          text: 'There is a beautiful painting ___ the wall.',
          options: [
            'on',
            'in',
            'at'
          ],
          answer: 'on',
          explanation: 'Walls are surfaces → \'on the wall\'.'
        },
        {
          text: 'I\'ll meet you ___ the entrance.',
          options: [
            'at',
            'in',
            'on'
          ],
          answer: 'at',
          explanation: 'An entrance is a specific point → \'at the entrance\'.'
        },
        {
          text: 'She left her keys ___ the drawer.',
          options: [
            'in',
            'at',
            'on'
          ],
          answer: 'in',
          explanation: 'A drawer is an enclosed space → \'in the drawer\'.'
        }
      ]
    },
    {
      title: 'Movement Prepositions',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct movement preposition (to, into, through, across, along).',
      questions: [
        {
          text: 'The cat jumped ___ the box.',
          answer: 'into',
          explanation: '\'Into\' indicates entering an enclosed space.'
        },
        {
          text: 'We walked ___ the bridge to the other side.',
          answer: 'across',
          explanation: '\'Across\' means moving from one side of a surface to the other.'
        },
        {
          text: 'The train went ___ the tunnel.',
          answer: 'through',
          explanation: '\'Through\' means passing from one end to the other of an enclosed passage.'
        },
        {
          text: 'They strolled ___ the riverbank enjoying the sunset.',
          answer: 'along',
          explanation: '\'Along\' means following a line or path.'
        }
      ]
    },
    {
      title: 'Error Correction',
      type: 'select',
      instructions: 'Which sentence contains the WRONG preposition?',
      questions: [
        {
          text: 'Which sentence has an error?',
          options: [
            'She arrived at Monday morning.',
            'She arrived on Monday morning.',
            'She arrived in the morning.'
          ],
          answer: 'She arrived at Monday morning.',
          explanation: 'Days use \'on\', not \'at\'. Correct: \'She arrived on Monday morning.\''
        },
        {
          text: 'Which sentence has an error?',
          options: [
            'He lives at London.',
            'He lives in London.',
            'He lives at 10 Downing Street.'
          ],
          answer: 'He lives at London.',
          explanation: 'Cities use \'in\', not \'at\'. Correct: \'He lives in London.\''
        },
        {
          text: 'Which sentence has an error?',
          options: [
            'The book is in the table.',
            'The book is on the table.',
            'The book is in the bag.'
          ],
          answer: 'The book is in the table.',
          explanation: 'Tables are surfaces → \'on the table\', not \'in the table\'.'
        },
        {
          text: 'Which sentence has an error?',
          options: [
            'We have a meeting on 3 pm.',
            'We have a meeting at 3 pm.',
            'We have a meeting on Friday.'
          ],
          answer: 'We have a meeting on 3 pm.',
          explanation: 'Precise times use \'at\', not \'on\'. Correct: \'at 3 pm\'.'
        }
      ]
    }
  ],
  tips: [
    'IN = enclosed/large areas + months/years/seasons',
    'ON = surfaces/days/dates',
    'AT = specific points/times',
    'Learn preposition + noun combinations: \'at home\', \'in bed\', \'on time\''
  ],
  dailyPractice: [
    'Practice basic prepositions concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Basic Prepositions: in/on/at for time and place. Key collocations and movement prepositions for IELTS accuracy.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...basicPrepositionsData,
      title: basicPrepositionsData.title + ' (Foundations)',
      overview: { ...basicPrepositionsData.overview, duration: '15-20 minutes' },
      sections: basicPrepositionsData.sections.slice(0, Math.max(2, Math.ceil(basicPrepositionsData.sections.length / 2))),
      exercises: basicPrepositionsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...basicPrepositionsData,
      title: basicPrepositionsData.title + ' (Advanced)',
      overview: { ...basicPrepositionsData.overview, duration: '30-35 minutes' },
      sections: basicPrepositionsData.sections,
      exercises: basicPrepositionsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(basicPrepositionsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return basicPrepositionsData;
}
